import { useState, useEffect, FormEvent } from 'react';
import { api, ApiError } from '../api/client';
import './TeamLogin.css';

interface TeamLoginProps {
  onLogin: () => void;
}

export function TeamLogin({ onLogin }: TeamLoginProps): JSX.Element {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [configured, setConfigured] = useState<boolean | null>(null);

  useEffect(() => {
    fetch('/api/team/status')
      .then(async (r) => {
        if (!r.ok) {
          setConfigured(true); // err on the side of letting them try
          return;
        }
        const body = (await r.json()) as { data?: { configured?: boolean } };
        setConfigured(body.data?.configured ?? true);
      })
      .catch(() => setConfigured(true));
  }, []);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api<{ role: string }>('/api/team/login', {
        method: 'POST',
        body: JSON.stringify({ password }),
      });
      onLogin();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'TEAM_PASSWORD_NOT_SET') {
          setError(
            'This page is not set up yet. Ask the admin to configure the team password in Settings.',
          );
          setConfigured(false);
        } else if (err.code === 'TOO_MANY_REQUESTS') {
          setError('Too many attempts. Please wait a few minutes and try again.');
        } else if (err.code === 'UNAUTHORIZED') {
          setError('Wrong password. Please try again.');
        } else {
          setError(err.message);
        }
      } else {
        setError('Connection error. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="team-login-container">
      <div className="team-login-card">
        <div className="team-login-logo">AP</div>
        <h1>Team — License Assignment</h1>
        <p>Enter the team password to continue.</p>

        {configured === false && (
          <div className="team-login-info">
            This page is not set up yet. Ask the admin to configure the team password in Settings.
          </div>
        )}

        <form className="team-login-form" onSubmit={handleSubmit}>
          {error && <div className="team-login-error">{error}</div>}
          <div className="team-login-field">
            <label htmlFor="team-password">Password</label>
            <input
              id="team-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              autoFocus
              required
              disabled={configured === false}
            />
          </div>
          <button
            type="submit"
            className="team-login-button"
            disabled={loading || configured === false}
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}
